import jwtDecode from 'jwt-decode';
import { BehaviorSubject } from 'rxjs';
import {
  EMBEDDED_CDS_ORDER_TOPIC,
  EMBEDDED_CDS_TIP_TOPIC,
  EMBEDDED_CDS_TOKEN_TOPIC,
  Order,
  OrderEvent,
  TipOption,
} from '@oolio-group/domain';
import { Session } from '../state/Session';
import {
  getSession,
  setEnableLocalComms,
  setSession,
} from '../state/preferences';
import { sessionSubject } from '../state/sessionObservable';

export interface EmbeddedConfig {
  apiUrl?: string;
  subscriptionUrl?: string;
  auditUrl?: string;
  healthCheckUrl?: string;
}

export interface EmbeddedBoot {
  session: Session;
  config?: EmbeddedConfig;
}

declare global {
  interface Window {
    __OOLIO_EMBEDDED__?: EmbeddedBoot;
  }
}

export const EMBEDDED_ORDER_TOPIC = EMBEDDED_CDS_ORDER_TOPIC;

export interface EmbeddedOrderMessage {
  order: Order | null;
  events: OrderEvent[];
}

export function parseEmbeddedOrderMessage(
  data: unknown,
): EmbeddedOrderMessage | undefined {
  try {
    const raw = typeof data === 'string' ? JSON.parse(data) : data;
    if (!raw || typeof raw !== 'object' || !('order' in raw)) return undefined;
    const { order, events } = raw as Partial<EmbeddedOrderMessage>;
    return {
      order: order?.id ? order : null,
      events: Array.isArray(events) ? events : [],
    };
  } catch {
    return undefined;
  }
}

export interface HostTokenResponse {
  accessToken: string;
  refreshToken?: string;
  expiresIn: number;
}

export const getEmbeddedBoot = (): EmbeddedBoot | undefined =>
  typeof window === 'undefined' ? undefined : window.__OOLIO_EMBEDDED__;

export const isEmbedded = (): boolean => !!getEmbeddedBoot();

export function seedEmbeddedSession(): void {
  const boot = getEmbeddedBoot();
  if (!boot) return;
  void setSession({
    ...boot.session,
    refreshToken: undefined,
    authorized: true,
  });
  void setEnableLocalComms(true);
  window.runtime?.EventsOn?.(EMBEDDED_CDS_TOKEN_TOPIC, token => {
    void applyHostToken(token);
  });
  window.runtime?.EventsOn?.(EMBEDDED_CDS_TIP_TOPIC, onTipMessage);
}

export interface EmbeddedTipRequest {
  requestId: string;
  amount: number;
  tipOptions?: TipOption[];
}

export type EmbeddedTipAnswer = { tipAmount: number } | { cancelled: true };

export const embeddedTipRequest$ =
  new BehaviorSubject<EmbeddedTipRequest | null>(null);

export function onTipMessage(data: unknown): void {
  const message = data as
    | (Partial<EmbeddedTipRequest> & { cancelled?: boolean })
    | null;
  if (!message?.requestId) return;
  if (message.cancelled) {
    if (embeddedTipRequest$.value?.requestId === message.requestId) {
      embeddedTipRequest$.next(null);
    }
    return;
  }
  if (typeof message.amount !== 'number') return;
  embeddedTipRequest$.next({
    requestId: message.requestId,
    amount: message.amount,
    tipOptions: message.tipOptions,
  });
}

export function answerTipRequest(
  requestId: string,
  answer: EmbeddedTipAnswer,
): void {
  if (embeddedTipRequest$.value?.requestId !== requestId) return;
  embeddedTipRequest$.next(null);
  void window.go?.main?.App?.SubmitTip?.({ requestId, ...answer });
}

export async function applyHostToken(token: unknown): Promise<void> {
  if (typeof token !== 'string' || !token) return;
  const session = await getSession();
  if (!session?.authorized || session.token === token) return;
  const { exp } = jwtDecode<{ exp: number }>(token);
  const next: Session = { ...session, token, expiredDate: exp * 1000 };
  sessionSubject.next(next);
  await setSession(next);
}

export async function refreshFromHost(): Promise<HostTokenResponse> {
  const getAccessToken = window.go?.main?.App?.GetAccessToken;
  if (!getAccessToken) throw new Error('Embedded host has no GetAccessToken');
  const accessToken = await getAccessToken();
  if (!accessToken) throw new Error('Embedded host returned no token');
  const { exp } = jwtDecode<{ exp: number }>(accessToken);
  return { accessToken, expiresIn: exp };
}
